import * as React from 'react';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import { useTranslation } from 'react-i18next';
import { observeArabicIndicDatePickerDigits } from 'i18n/arabicDatePickerDigits';
import { buildAntdLocale } from 'i18n/calendarLabels';
import { getLocaleLayoutDirection } from 'i18n/locales';

type AntdLocaleProviderProps = {
  children: React.ReactNode;
};

export const AntdLocaleProvider: React.FC<AntdLocaleProviderProps> = ({
  children,
}) => {
  const { t, i18n } = useTranslation();
  const layoutDirection = getLocaleLayoutDirection(i18n.language);
  const isArabic = i18n.language.startsWith('ar');

  React.useEffect(() => {
    // Ant Design DatePicker panel headers (e.g. "July 2026") use dayjs locales.
    dayjs.locale(isArabic ? 'ar' : 'en');
  }, [isArabic]);

  React.useEffect(() => {
    if (!isArabic) return;
    // Calendar cells / header year render raw JS numbers — convert in the open panel.
    return observeArabicIndicDatePickerDigits();
  }, [isArabic]);

  const antdLocale = React.useMemo(
    () => buildAntdLocale(t, i18n.language),
    [t, i18n.language]
  );

  return (
    <ConfigProvider locale={antdLocale} direction={layoutDirection}>
      {children}
    </ConfigProvider>
  );
};
